import React, { useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  History as HistoryIcon,
  DirectionsCar as TruckIcon,
  Person as PersonIcon,
  RvHookup as TrailerIcon,
  SupportAgent as DispatcherIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import { useGetTruckDriversQuery, useGetDispatchersQuery } from "../../api/employeesApi";
import { useGetVehiclesQuery } from "../../api/vehiclesApi";
import { useGetTrailersQuery } from "../../api/trailersApi";
import { useGetAllVehicleAssignmentsQuery, useUpdateVehicleAssignmentsMutation } from "../../api/vehicleAssignmentsApi";
import { useGetAllTrailerAssignmentsQuery, useUpdateTrailerAssignmentsMutation } from "../../api/trailerAssignmentsApi";
import { useGetAllDispatcherAssignmentsQuery, useUpdateDispatcherAssignmentsMutation } from "../../api/dispatcherAssignmentsApi";
import PageTitle from "../../components/common/PageTitle";
import { AssignmentIndIcon } from "../../components/common/pageIcons";
import PageLayout from "../../components/layout/PageLayout";
import AuditHistoryDialog from "../audit/components/AuditHistoryDialog";

// ─── Types ──────────────────────────────────────────────────────────────────

interface OptionItem {
  id: number;
  label: string;
}

type SortColumn = "driver" | "truck" | "trailer" | "dispatcher";
type SortDir = "asc" | "desc";

// ─── Main Page ───────────────────────────────────────────────────────────────

const DriverAssignmentsPage: React.FC = () => {
  const [search, setSearch] = useState("");
  const [sortCol, setSortCol] = useState<SortColumn>("driver");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [historyDriver, setHistoryDriver] = useState<{ id: number; name: string } | null>(null);

  const { data: drivers = [], isLoading: driversLoading, refetch: refetchDrivers } = useGetTruckDriversQuery();
  const { data: dispatchers = [], isLoading: dispatchersLoading } = useGetDispatchersQuery();
  const { data: vehicles = [], isLoading: vehiclesLoading } = useGetVehiclesQuery();
  const { data: trailers = [], isLoading: trailersLoading } = useGetTrailersQuery();
  const { data: vehicleAssignments = [], isLoading: vaLoading, refetch: refetchVA } = useGetAllVehicleAssignmentsQuery();
  const { data: trailerAssignments = [], isLoading: taLoading, refetch: refetchTA } = useGetAllTrailerAssignmentsQuery();
  const { data: dispatcherAssignments = [], isLoading: daLoading, refetch: refetchDA } = useGetAllDispatcherAssignmentsQuery();

  const isLoading = driversLoading || dispatchersLoading || vehiclesLoading || trailersLoading || vaLoading || taLoading || daLoading;

  const handleRefresh = () => {
    refetchDrivers();
    refetchVA();
    refetchTA();
    refetchDA();
  };

  // Build sets of assigned EmployeeDriverInfoIds for the unassigned summary
  const assignedDriverInfoIds = useMemo(
    () => new Set([
      ...vehicleAssignments.map(a => a.employeeId),
      ...trailerAssignments.map(a => a.employeeId),
      ...dispatcherAssignments.map(a => a.employeeDriverInfoId),
    ]),
    [vehicleAssignments, trailerAssignments, dispatcherAssignments]
  );

  const assignedVehicleIds = useMemo(
    () => new Set(vehicleAssignments.map(a => a.vehicleId)),
    [vehicleAssignments]
  );

  const assignedTrailerIds = useMemo(
    () => new Set(trailerAssignments.map(a => a.trailerId)),
    [trailerAssignments]
  );

  const assignedDispatcherIds = useMemo(
    () => new Set(dispatcherAssignments.map(a => a.dispatcherEmployeeId)),
    [dispatcherAssignments]
  );

  const unassignedDrivers = drivers.filter(d => !assignedDriverInfoIds.has(d.id));
  const unassignedVehicles = vehicles.filter(v => !assignedVehicleIds.has(v.id));
  const unassignedTrailers = trailers.filter(
    (t) => !assignedTrailerIds.has(t.id) && !t.isArchived
  );
  const unassignedDispatchers = dispatchers.filter(d => !assignedDispatcherIds.has(d.id));

  // Build lookup maps keyed by EmployeeDriverInfoId
  const driverVehicleMap = useMemo(() => {
    const map = new Map<number, number[]>();
    for (const a of vehicleAssignments) {
      if (!map.has(a.employeeId)) map.set(a.employeeId, []);
      map.get(a.employeeId)!.push(a.vehicleId);
    }
    return map;
  }, [vehicleAssignments]);

  const driverTrailerMap = useMemo(() => {
    const map = new Map<number, number[]>();
    for (const a of trailerAssignments) {
      if (!map.has(a.employeeId)) map.set(a.employeeId, []);
      map.get(a.employeeId)!.push(a.trailerId);
    }
    return map;
  }, [trailerAssignments]);

  const driverDispatcherMap = useMemo(() => {
    const map = new Map<number, number[]>();
    for (const a of dispatcherAssignments) {
      if (!map.has(a.employeeDriverInfoId)) map.set(a.employeeDriverInfoId, []);
      map.get(a.employeeDriverInfoId)!.push(a.dispatcherEmployeeId);
    }
    return map;
  }, [dispatcherAssignments]);

  // Option lists
  const vehicleOptions: OptionItem[] = vehicles.map(v => ({ id: v.id, label: v.name || `Vehicle #${v.id}` }));
  const trailerOptions: OptionItem[] = trailers
    .filter((t) => !t.isArchived || assignedTrailerIds.has(t.id))
    .map((t) => ({ id: t.id, label: t.unitNumber || `Trailer #${t.id}` }));
  const selectableTrailerOptions: OptionItem[] = trailers
    .filter((t) => !t.isArchived)
    .map((t) => ({ id: t.id, label: t.unitNumber || `Trailer #${t.id}` }));
  const dispatcherOptions: OptionItem[] = dispatchers.map(d => ({ id: d.id, label: `${d.firstName} ${d.lastName}` }));

  // Build flat rows with resolved labels (for search + sort)
  const allRows = useMemo(() => drivers.map(driver => {
    const vehicleIds = driverVehicleMap.get(driver.id) ?? [];
    const trailerIds = driverTrailerMap.get(driver.id) ?? [];
    const dispatcherIds = driverDispatcherMap.get(driver.id) ?? [];
    const vehicleLabels = vehicleOptions.filter(o => vehicleIds.includes(o.id)).map(o => o.label);
    const trailerLabels = trailerOptions.filter(o => trailerIds.includes(o.id)).map(o => o.label);
    const dispatcherLabels = dispatcherOptions.filter(o => dispatcherIds.includes(o.id)).map(o => o.label);
    return {
      driver,
      vehicleIds,
      trailerIds,
      dispatcherIds,
      vehicleLabels,
      trailerLabels,
      dispatcherLabels,
      driverName: `${driver.firstName} ${driver.lastName}`,
    };
  }), [drivers, driverVehicleMap, driverTrailerMap, driverDispatcherMap, vehicleOptions, trailerOptions, dispatcherOptions]);

  // Filter by search term
  const filteredRows = useMemo(() => {
    if (!search.trim()) return allRows;
    const q = search.toLowerCase();
    return allRows.filter(r =>
      r.driverName.toLowerCase().includes(q) ||
      r.vehicleLabels.some(l => l.toLowerCase().includes(q)) ||
      r.trailerLabels.some(l => l.toLowerCase().includes(q)) ||
      r.dispatcherLabels.some(l => l.toLowerCase().includes(q))
    );
  }, [allRows, search]);

  // Sort
  const sortedRows = useMemo(() => {
    const colKey = (r: typeof filteredRows[0]) => {
      switch (sortCol) {
        case "driver":     return r.driverName;
        case "truck":      return r.vehicleLabels[0] ?? "";
        case "trailer":    return r.trailerLabels[0] ?? "";
        case "dispatcher": return r.dispatcherLabels[0] ?? "";
      }
    };
    return [...filteredRows].sort((a, b) => {
      const cmp = colKey(a).localeCompare(colKey(b), undefined, { sensitivity: "base" });
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [filteredRows, sortCol, sortDir]);

  const handleSort = (col: SortColumn) => {
    if (col === sortCol) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortCol(col);
      setSortDir("asc");
    }
  };

  return (
    <PageLayout>
      {/* Header */}
      <PageTitle
        title="Driver Assignments"
        icon={<AssignmentIndIcon />}
        actions={
          <Tooltip title="Refresh data">
            <IconButton onClick={handleRefresh} disabled={isLoading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        }
      />

      {/* Unassigned Summary */}
      <UnassignedSummary
        isLoading={isLoading}
        unassignedDrivers={unassignedDrivers.map(d => `${d.firstName} ${d.lastName}`)}
        unassignedVehicles={unassignedVehicles.map(v => v.name || `#${v.id}`)}
        unassignedTrailers={unassignedTrailers.map(t => t.unitNumber || `#${t.id}`)}
        unassignedDispatchers={unassignedDispatchers.map(d => `${d.firstName} ${d.lastName}`)}
      />

      {/* Assignment Table */}
      <Box sx={{ mb: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 500 }}>
          Assignments
        </Typography>
        <TextField
          size="small"
          placeholder="Search driver, truck, trailer, dispatcher…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ width: 340 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch("")}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      </Box>

      {isLoading ? (
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 1 }} />
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "action.hover" }}>
                {(["driver", "truck", "trailer", "dispatcher"] as SortColumn[]).map(col => (
                  <TableCell key={col} sx={{ fontWeight: 600 }}>
                    <TableSortLabel
                      active={sortCol === col}
                      direction={sortCol === col ? sortDir : "asc"}
                      onClick={() => handleSort(col)}
                    >
                      {col === "driver" ? "Driver" : col === "truck" ? "Truck(s)" : col === "trailer" ? "Trailer(s)" : "Dispatcher(s)"}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell sx={{ fontWeight: 600, width: 100 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {drivers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4, color: "text.secondary" }}>
                    No drivers found. Add drivers in Users management.
                  </TableCell>
                </TableRow>
              ) : sortedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4, color: "text.secondary" }}>
                    No results for "{search}".
                  </TableCell>
                </TableRow>
              ) : (
                sortedRows.map(row => (
                  <AssignmentRow
                    key={row.driver.id}
                    driverId={row.driver.id}
                    driverName={row.driverName}
                    currentVehicleIds={row.vehicleIds}
                    currentTrailerIds={row.trailerIds}
                    currentDispatcherIds={row.dispatcherIds}
                    vehicleOptions={vehicleOptions}
                    trailerOptions={trailerOptions}
                    selectableTrailerOptions={selectableTrailerOptions}
                    dispatcherOptions={dispatcherOptions}
                    onSaved={handleRefresh}
                    onViewHistory={() => setHistoryDriver({ id: row.driver.id, name: row.driverName })}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {historyDriver && (
        <AuditHistoryDialog
          open={Boolean(historyDriver)}
          entityType="EmployeeDriverInfo"
          entityId={historyDriver.id}
          title={`History for ${historyDriver.name}`}
          onClose={() => setHistoryDriver(null)}
        />
      )}
    </PageLayout>
  );
};

// ─── Unassigned Summary Panel ─────────────────────────────────────────────────

interface UnassignedSummaryProps {
  isLoading: boolean;
  unassignedDrivers: string[];
  unassignedVehicles: string[];
  unassignedTrailers: string[];
  unassignedDispatchers: string[];
}

const UnassignedSummary: React.FC<UnassignedSummaryProps> = ({
  isLoading,
  unassignedDrivers,
  unassignedVehicles,
  unassignedTrailers,
  unassignedDispatchers,
}) => {
  const sections = [
    { label: "Unassigned Drivers", icon: <PersonIcon fontSize="small" />, items: unassignedDrivers, color: "primary" as const },
    { label: "Unassigned Trucks", icon: <TruckIcon fontSize="small" />, items: unassignedVehicles, color: "warning" as const },
    { label: "Unassigned Trailers", icon: <TrailerIcon fontSize="small" />, items: unassignedTrailers, color: "secondary" as const },
    { label: "Unassigned Dispatchers", icon: <DispatcherIcon fontSize="small" />, items: unassignedDispatchers, color: "success" as const },
  ];

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 500, mb: 1.5 }}>
        Unassigned
      </Typography>
      <Grid container spacing={2}>
        {sections.map(section => (
          <Grid item xs={12} sm={6} md={3} key={section.label}>
            <Paper variant="outlined" sx={{ p: 2, minHeight: 100 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
                <Box sx={{ color: `${section.color}.main` }}>{section.icon}</Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {section.label}
                </Typography>
                <Chip
                  label={isLoading ? "…" : section.items.length}
                  size="small"
                  color={section.items.length > 0 ? section.color : "default"}
                  sx={{ ml: "auto", fontSize: "0.7rem", height: 18 }}
                />
              </Box>
              {isLoading ? (
                <Skeleton variant="text" width="60%" />
              ) : section.items.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8rem" }}>
                  All assigned
                </Typography>
              ) : (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                  {section.items.slice(0, 8).map(item => (
                    <Chip
                      key={item}
                      label={item}
                      size="small"
                      variant="outlined"
                      color={section.color}
                      sx={{ fontSize: "0.7rem", height: 22 }}
                    />
                  ))}
                  {section.items.length > 8 && (
                    <Chip
                      label={`+${section.items.length - 8} more`}
                      size="small"
                      sx={{ fontSize: "0.7rem", height: 22 }}
                    />
                  )}
                </Box>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

// ─── Assignment Row ───────────────────────────────────────────────────────────

interface AssignmentRowProps {
  driverId: number;
  driverName: string;
  currentVehicleIds: number[];
  currentTrailerIds: number[];
  currentDispatcherIds: number[];
  vehicleOptions: OptionItem[];
  trailerOptions: OptionItem[];
  selectableTrailerOptions: OptionItem[];
  dispatcherOptions: OptionItem[];
  onSaved: () => void;
  onViewHistory: () => void;
}

const AssignmentRow: React.FC<AssignmentRowProps> = ({
  driverId,
  driverName,
  currentVehicleIds,
  currentTrailerIds,
  currentDispatcherIds,
  vehicleOptions,
  trailerOptions,
  selectableTrailerOptions,
  dispatcherOptions,
  onSaved,
  onViewHistory,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedVehicles, setSelectedVehicles] = useState<OptionItem[]>([]);
  const [selectedTrailers, setSelectedTrailers] = useState<OptionItem[]>([]);
  const [selectedDispatchers, setSelectedDispatchers] = useState<OptionItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [updateVehicleAssignments, { isLoading: savingVehicles }] = useUpdateVehicleAssignmentsMutation();
  const [updateTrailerAssignments, { isLoading: savingTrailers }] = useUpdateTrailerAssignmentsMutation();
  const [updateDispatcherAssignments, { isLoading: savingDispatchers }] = useUpdateDispatcherAssignmentsMutation();

  const isSaving = savingVehicles || savingTrailers || savingDispatchers;

  const handleEdit = () => {
    setSelectedVehicles(vehicleOptions.filter(o => currentVehicleIds.includes(o.id)));
    setSelectedTrailers(trailerOptions.filter(o => currentTrailerIds.includes(o.id)));
    setSelectedDispatchers(dispatcherOptions.filter(o => currentDispatcherIds.includes(o.id)));
    setError(null);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setError(null);
  };

  const handleSave = async () => {
    setError(null);
    try {
      await Promise.all([
        updateVehicleAssignments({ driverId, vehicleIds: selectedVehicles.map(v => v.id) }).unwrap(),
        updateTrailerAssignments({ driverId, trailerIds: selectedTrailers.map(t => t.id) }).unwrap(),
        updateDispatcherAssignments({ driverId, dispatcherEmployeeIds: selectedDispatchers.map(d => d.id) }).unwrap(),
      ]);
      setIsEditing(false);
      onSaved();
    } catch {
      setError("Failed to save assignments. Please try again.");
    }
  };

  const vehicleLabels = vehicleOptions
    .filter(o => currentVehicleIds.includes(o.id))
    .map(o => o.label);

  const trailerLabels = trailerOptions
    .filter(o => currentTrailerIds.includes(o.id))
    .map(o => o.label);

  const dispatcherLabels = dispatcherOptions
    .filter(o => currentDispatcherIds.includes(o.id))
    .map(o => o.label);

  return (
    <>
      <TableRow hover sx={{ verticalAlign: "top" }}>
        {/* Driver */}
        <TableCell sx={{ py: 1.5, fontWeight: 500, minWidth: 140 }}>
          {driverName}
        </TableCell>

        {/* Trucks */}
        <TableCell sx={{ py: 1, minWidth: 200 }}>
          {isEditing ? (
            <Autocomplete
              multiple
              size="small"
              options={vehicleOptions}
              getOptionLabel={o => o.label}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              value={selectedVehicles}
              onChange={(_, val) => setSelectedVehicles(val)}
              renderInput={params => <TextField {...params} placeholder="Select trucks" />}
              renderTags={(val, getTagProps) =>
                val.map((option, index) => (
                  <Chip
                    label={option.label}
                    size="small"
                    color="primary"
                    variant="outlined"
                    {...getTagProps({ index })}
                    key={option.id}
                  />
                ))
              }
            />
          ) : (
            <ChipList labels={vehicleLabels} color="primary" emptyText="None" />
          )}
        </TableCell>

        {/* Trailers */}
        <TableCell sx={{ py: 1, minWidth: 200 }}>
          {isEditing ? (
            <Autocomplete
              multiple
              size="small"
              options={selectableTrailerOptions}
              getOptionLabel={o => o.label}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              value={selectedTrailers}
              onChange={(_, val) => setSelectedTrailers(val)}
              renderInput={params => <TextField {...params} placeholder="Select trailers" />}
              renderTags={(val, getTagProps) =>
                val.map((option, index) => (
                  <Chip
                    label={option.label}
                    size="small"
                    color="secondary"
                    variant="outlined"
                    {...getTagProps({ index })}
                    key={option.id}
                  />
                ))
              }
            />
          ) : (
            <ChipList labels={trailerLabels} color="secondary" emptyText="None" />
          )}
        </TableCell>

        {/* Dispatchers */}
        <TableCell sx={{ py: 1, minWidth: 200 }}>
          {isEditing ? (
            <Autocomplete
              multiple
              size="small"
              options={dispatcherOptions}
              getOptionLabel={o => o.label}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              value={selectedDispatchers}
              onChange={(_, val) => setSelectedDispatchers(val)}
              renderInput={params => <TextField {...params} placeholder="Select dispatchers" />}
              renderTags={(val, getTagProps) =>
                val.map((option, index) => (
                  <Chip
                    label={option.label}
                    size="small"
                    color="success"
                    variant="outlined"
                    {...getTagProps({ index })}
                    key={option.id}
                  />
                ))
              }
            />
          ) : (
            <ChipList labels={dispatcherLabels} color="success" emptyText="None" />
          )}
        </TableCell>

        {/* Actions */}
        <TableCell sx={{ py: 1, whiteSpace: "nowrap" }}>
          {isEditing ? (
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <Tooltip title="Save changes">
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    {isSaving ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Cancel">
                <IconButton size="small" onClick={handleCancel} disabled={isSaving}>
                  <CancelIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ) : (
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <Tooltip title="Edit assignments">
                <IconButton size="small" onClick={handleEdit}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="View driver history">
                <IconButton size="small" color="info" onClick={onViewHistory}>
                  <HistoryIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </TableCell>
      </TableRow>

      {/* Error row */}
      {error && (
        <TableRow>
          <TableCell colSpan={5} sx={{ py: 0, pb: 1 }}>
            <Alert severity="error" sx={{ py: 0 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

// ─── Chip List helper ─────────────────────────────────────────────────────────

interface ChipListProps {
  labels: string[];
  color: "primary" | "secondary" | "success" | "warning";
  emptyText: string;
}

const ChipList: React.FC<ChipListProps> = ({ labels, color, emptyText }) => {
  if (labels.length === 0) {
    return (
      <Typography variant="body2" color="text.disabled" sx={{ fontSize: "0.8rem" }}>
        {emptyText}
      </Typography>
    );
  }
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, py: 0.5 }}>
      {labels.map(label => (
        <Chip
          key={label}
          label={label}
          size="small"
          color={color}
          variant="outlined"
          sx={{ fontSize: "0.75rem", height: 22 }}
        />
      ))}
    </Box>
  );
};

export default DriverAssignmentsPage;
